import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  FrameItem,
  GenericType,
  ExternalType
} from '@interface/index';
import { NiTable, BodyGroup, AddButton, NiModal, NiForm, NiInput } from '@component/index';
import { API, SELECTION_KEY, SelectionOption } from '@config/index';
import { isEqual, ParameterMapStore } from '@lib/index';
import { localizedString } from '@locale/index';
import { message } from 'antd';
import { cloneDeep, debounce } from 'lodash';
import EmptyLine from '@component/business-component/empty-line/empty-line';

type GenericOrExternalTypeCompProps = {
  disabled?: boolean;
  frame: FrameItem;
  type?: 'generic' | 'external'; // default is generic
  data: GenericType[] | ExternalType[];
  onChange: (next: GenericType[] | ExternalType[]) => void;
};

interface GenericOrExternalTypeCompState {
  data: GenericType[] | ExternalType[];
  curRecord?: GenericType | ExternalType;
  modalVisible: boolean;
  modalIndex: number;
}

export class GenericOrExternalTypeComp extends React.Component<GenericOrExternalTypeCompProps, GenericOrExternalTypeCompState> {
  constructor(props: GenericOrExternalTypeCompProps) {
    super(props);

    this.state = {
      data: props.data,
      modalVisible: false,
      modalIndex: -1
    };
  }

  componentDidMount() {
    this.onInit();
  }

  componentDidUpdate(prevProps: GenericOrExternalTypeCompProps, prevState: GenericOrExternalTypeCompState) {

    if (!isEqual(prevProps.data, this.props.data)) {
      this.onInit();
    }
  }

  onInit = () => {
    this.setState({ data: cloneDeep(this.props.data) });
  }

  render() {
    const {
      data,
      modalVisible,
      curRecord
    } = this.state;
    const {
      disabled,
      type = 'generic'
    } = this.props;

    return <BodyGroup
      foldable
      title={localizedString(type === 'external'
        ? 'external_type'
        : 'generic_type')}
      extra={data && data.filter(item => !item.toBeDeleted).length && <AddButton
        disabled={disabled}
        onClick={this.handleNew}
      /> || ''}
    > {data && data.filter(item => !item.toBeDeleted).length &&
      <NiTable className="generic-type-table"
        showHeader={false}
        rowKey={((record: GenericType | ExternalType) =>
          `generic-type-row-${record.id}`)}
        dataSource={data}
        onRow={(item: GenericType | ExternalType, index?: number) => ({
          onClick: () => {
            if (index === undefined) {
              return;
            }

            this.setState({
              curRecord: cloneDeep(data[index]),
              modalIndex: index,
              modalVisible: true
            });
          }
        })}
        pagination={false}
        columns={this.getColumns()}
      /> || <EmptyLine text={`click to add a new ${type === 'external' ? 'external type' : 'generic type'}`} onClick={this.handleNew} />
      }
      <NiModal
        title={localizedString(type === 'external'
          ? 'external_type'
          : 'generic_type')}
        open={modalVisible}
        onOk={this.handleModalConfirm}
        okText={localizedString('common_add')}
        okButtonProps={disabled || (curRecord && curRecord.inheritedFromCompositeId) ? {
          type: 'ghost',
          disabled: true
        } : undefined
        }
        onCancel={this.handleModalClose}
      >
        <NiForm
          titleline
          direction="vertical"
          items={[
            {
              key: 'import',
              label: <label style={{ marginTop: 4 }}>{localizedString(type === 'external'
                ? 'external_type_label'
                : 'generic_type_label')}</label>,
              content: <NiInput
                placeholder={localizedString(type === 'external'
                  ? 'enter_external_type_label'
                  : 'enter_generic_type_label')}
                value={curRecord && curRecord.name}
                onChange={(e: any) => {
                  this.setState({
                    curRecord: {
                      ...curRecord,
                      name: e.target.value
                    }
                  });
                }}
                style={{ width: '100%' }}
              />
            }]} />
      </NiModal>
    </BodyGroup>;
  }

  getColumns = () => {
    const { onChange, frame, disabled } = this.props;
    const { data } = this.state;
    return [{
      width: '90%',
      title: localizedString('generic_type'),
      dataIndex: 'name'
    },
    {
      width: '10%',
      title: 'Source',
      dataIndex: 'inheritedFromCompositeName',
      key: 'source',
      render: (sourceName: string, record: GenericType | ExternalType) => {
        return sourceName || (record.isOverwrite ? 'Local+' : 'Local');
      }
    },
    {
      title: '',
      width: 50,
      dataIndex: 'operation',
      className: 'hide',
      render: (operation: any, record: GenericType | ExternalType, index: number) => (
        <div
          className="icon-area"
        >
          {!disabled &&
            !record.inheritedFromCompositeId &&
            <DeleteOutlined className="delete-btn" onClick={(e: any) => {
              e.stopPropagation();
              this.handleDelete(index);
            }} />
          }
        </div>
      )
    }];
  }

  handleNew = () => {
    const { data } = this.state;

    this.setState({
      curRecord: {
        name: ''
      },
      modalIndex: -1,
      modalVisible: true
    });
  }

  handleModalConfirm = debounce(() => {
    const { onChange } = this.props;
    const { modalIndex, curRecord, data = [] } = this.state;

    if (!curRecord) {
      return;
    }

    if (!curRecord.name || !curRecord?.name.length) {
      message.warning(localizedString('generic_type_name_required'));
      return;
    }

    if (curRecord.inheritedFromCompositeId) {
      return;
    }

    const dupItem = data.find((item: GenericType | ExternalType, index: number) =>
      modalIndex === index && item.name === curRecord.name);
    if (dupItem) {
      message.warning(localizedString('generic_type_name_duplicated'));
      return;
    }

    curRecord.name = curRecord.name.trim();

    const nextData = cloneDeep(data);

    if (modalIndex === -1) {
      nextData.push(curRecord);
    } else {
      nextData.splice(modalIndex, 1, curRecord);
    }

    this.handleModalClose();
    onChange(nextData);
  }, 500);

  handleModalClose = () => {
    this.setState({
      modalVisible: false,
      modalIndex: -1,
      curRecord: undefined
    });
  }

  handleDelete = async (index: number) => {
    const { type } = this.props;
    const { data } = this.state;

    const delItem = data[index];

    if (delItem.id) {

      if (type === 'external') {
        await API.common.checkExternalTypeDependency(delItem.id || '')
          .catch((err: Error) => {
            message.warning(err.message);
            throw err;
          });
      } else {
        await API.common.checkGenericTypeDependency(delItem.id || '')
          .catch((err: Error) => {
            message.warning(err.message);
            throw err;
          });
      }
    }

    const nextData = cloneDeep(data);
    nextData.splice(index, 1);
    this.setState({ data: nextData });
    this.props.onChange(nextData);
  }
}
